import type {
  User,
  AttendanceRecord,
  CheckInRecord,
  CheckOutRecord,
  MonthlySummary,
  LeaveRequest,
  OvertimeRequest,
  SalaryAdvanceRequest,
  ShiftSwapRequest,
  Colleague,
} from '../types';
import { BASE_URL } from './api';

function resolvePhotoUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  return `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

type ApiRecord = Record<string, unknown>;

// ─── User / Employee Profile ─────────────────────────────────────────────────

export function mapUser(data: ApiRecord): User {
  return {
    id: String(data.employee_id ?? data.id ?? ''),
    uuid: String(data.uuid ?? ''),
    code: String(data.code ?? ''),
    fullName: String(data.full_name ?? ''),
    email: String(data.email ?? ''),
    gender: String(data.gender ?? ''),
    phone: data.phone ? String(data.phone) : undefined,
    address: data.address ? String(data.address) : undefined,
    zipCode: data.zip_code ? String(data.zip_code) : undefined,
    birthDate: data.birth_date ? String(data.birth_date) : undefined,
    joinedDate: data.joined_date ? String(data.joined_date) : undefined,
    departmentId: String(data.department_id ?? ''),
    departmentName: data.department_name ? String(data.department_name) : undefined,
    organizationId: String(data.organization_id ?? ''),
    status: (data.status as 'active' | 'inactive') ?? 'active',
    allowOutsideRadius: Boolean(data.enable_attendance_outside_radius ?? false),
  };
}

// ─── Attendance ───────────────────────────────────────────────────────────────

function mapCheckInRecord(record: ApiRecord, attendanceDate?: string): CheckInRecord {
  const recordedAt = String(record.recorded_at ?? '');
  // If recordedAt is time-only, combine with attendance date
  const fullTimestamp = attendanceDate && recordedAt && !recordedAt.includes('T')
    ? `${attendanceDate}T${recordedAt}`
    : recordedAt;

  return {
    id: String(record.recorded_at ?? ''),
    latitude: Number(record.latitude ?? 0),
    longitude: Number(record.longitude ?? 0),
    accuracy: Number(record.accuracy_m ?? 0),
    distance: Number(record.distance_m ?? 0),
    withinGeofence: Boolean(record.within_geofence),
    lateMinutes: Number(record.late_minutes ?? 0),
    recordedAt: fullTimestamp,
    photoDataUrl: resolvePhotoUrl(record.photo_url as string | null | undefined),
  };
}

function mapCheckOutRecord(record: ApiRecord, attendanceDate?: string, isOvernight?: boolean): CheckOutRecord {
  let recordedAt = String(record.recorded_at ?? '');

  // If recordedAt is time-only, combine with attendance date
  if (attendanceDate && recordedAt && !recordedAt.includes('T')) {
    if (isOvernight) {
      // Add one day for overnight shifts
      const nextDate = new Date(attendanceDate);
      nextDate.setDate(nextDate.getDate() + 1);
      const nextDateStr = nextDate.toISOString().split('T')[0];
      recordedAt = `${nextDateStr}T${recordedAt}`;
    } else {
      recordedAt = `${attendanceDate}T${recordedAt}`;
    }
  }

  return {
    id: String(record.recorded_at ?? ''),
    latitude: Number(record.latitude ?? 0),
    longitude: Number(record.longitude ?? 0),
    accuracy: Number(record.accuracy_m ?? 0),
    distance: Number(record.distance_m ?? 0),
    withinGeofence: Boolean(record.within_geofence),
    earlyLeaveMinutes: Number(record.early_leave_minutes ?? 0),
    recordedAt,
    photoDataUrl: resolvePhotoUrl(record.photo_url as string | null | undefined),
  };
}

export function mapAttendanceRecord(data: ApiRecord): AttendanceRecord {
  const date = String(data.date ?? '');
  const clockIn = data.clock_in ? String(data.clock_in) : null;
  const clockOut = data.clock_out ? String(data.clock_out) : null;
  const shift = data.shift as ApiRecord | null;
  const site = data.site as ApiRecord | null;
  const checkInRaw = data.check_in_record as ApiRecord | null;
  const checkOutRaw = data.check_out_record as ApiRecord | null;

  // Helper to parse time string (HH:mm:ss) to minutes
  const timeToMinutes = (timeStr: string): number => {
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
  };

  // Handle clockIn timestamp
  const clockInTimestamp = clockIn
    ? clockIn.includes('T') ? clockIn : `${date}T${clockIn}`
    : date;

  // Detect overnight shift (checkout time < checkin time)
  const isOvernight = !!(clockIn && clockOut && !clockOut.includes('T') &&
    timeToMinutes(clockOut) < timeToMinutes(clockIn.split('T').pop()?.split('.')[0] ?? ''));

  // Handle clockOut timestamp - detect overnight shifts
  let clockOutTimestamp: string | null = null;
  if (clockOut) {
    if (clockOut.includes('T')) {
      clockOutTimestamp = clockOut;
    } else if (isOvernight) {
      // Add one day to the date for overnight shifts
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);
      const nextDateStr = nextDate.toISOString().split('T')[0];
      clockOutTimestamp = `${nextDateStr}T${clockOut}`;
    } else {
      clockOutTimestamp = `${date}T${clockOut}`;
    }
  }
  const shiftSchedule = {
    name: (shift?.name ?? '') as string,
    startTime: (shift?.start_time ?? '') as string,
    endTime: (shift?.end_time ?? '') as string,
    isSaturdayHalfDay: Boolean(shift?.is_saturday_half_day),
    saturdayEndTime: (shift?.saturday_end_time ?? '') as string,
  }

  return {
    id: String(data.id ?? ''),
    uuid: String(data.id ?? ''),
    organizationId: '',
    siteId: String(site?.id ?? ''),
    employeeId: '',
    shiftId: String(shift?.id ?? ''),
    shift: shiftSchedule,
    date,
    clockIn: clockInTimestamp,
    clockOut: clockOutTimestamp,
    status: data.status === 'late' ? 'late' : 'on_time',
    checkInRecord: checkInRaw ? mapCheckInRecord(checkInRaw, date) : null,
    checkOutRecord: checkOutRaw ? mapCheckOutRecord(checkOutRaw, date, isOvernight) : null,
  };
}

export function mapMonthlySummary(data: ApiRecord): MonthlySummary {
  // Ensure hours are never negative (defensive against backend calculation issues)
  const totalHours = Math.max(0, Number(data.total_hours ?? 0));
  const avgHoursPerDay = Math.max(0, Number(data.avg_hours_per_day ?? 0));

  return {
    totalDays: Number(data.total_days ?? 0),
    onTimeDays: Number(data.total_present ?? 0),
    lateDays: Number(data.total_late ?? 0),
    totalHours,
    totalMinutes: Number(data.total_minutes ?? 0),
    avgHoursPerDay,
  };
}

// ─── Requests ─────────────────────────────────────────────────────────────────

export function mapLeaveRequest(data: ApiRecord): LeaveRequest {
  return {
    id: String(data.id ?? ''),
    employeeId: '',
    leaveType: String(data.leave_type ?? ''),
    startDate: String(data.start_date ?? ''),
    endDate: String(data.end_date ?? ''),
    totalDays: Number(data.total_days ?? 1),
    reason: String(data.reason ?? ''),
    status: (data.status as LeaveRequest['status']) ?? 'pending',
    submittedAt: String(data.created_at ?? ''),
  };
}

export function mapOvertimeRequest(data: ApiRecord): OvertimeRequest {
  return {
    id: String(data.id ?? ''),
    employeeId: '',
    date: String(data.date ?? ''),
    totalHours: Number(data.hours ?? 0),
    status: (data.status as OvertimeRequest['status']) ?? 'pending',
    submittedAt: String(data.created_at ?? ''),
  };
}

export function mapSalaryAdvance(data: ApiRecord): SalaryAdvanceRequest {
  return {
    id: String(data.id ?? ''),
    employeeId: '',
    amount: Number(data.amount ?? 0),
    reason: String(data.reason ?? ''),
    status: (data.status as SalaryAdvanceRequest['status']) ?? 'pending',
    submittedAt: String(data.created_at ?? ''),
  };
}

export function mapShiftSwapRequest(data: ApiRecord): ShiftSwapRequest {
  return {
    id: String(data.id ?? ''),
    swapDate: String(data.swap_date ?? ''),
    reason: data.reason ? String(data.reason) : null,
    status: (data.status as ShiftSwapRequest['status']) ?? 'pending',
    rejectionReason: data.rejection_reason ? String(data.rejection_reason) : null,
    requesterId: String(data.requester_id ?? ''),
    requesterName: String(data.requester_name ?? ''),
    requesterShiftName: String(data.requester_shift_name ?? ''),
    targetId: String(data.target_id ?? ''),
    targetName: String(data.target_name ?? ''),
    targetShiftName: String(data.target_shift_name ?? ''),
    approvedBy: data.approved_by ? String(data.approved_by) : null,
    submittedAt: String(data.created_at ?? ''),
  };
}

export function mapColleague(data: ApiRecord): Colleague {
  console.log(data);
  return {
    id: Number(data.id ?? 0),
    fullName: String(data.full_name ?? ''),
    code: String(data.code ?? ''),
    shiftId: data.shift_id != null ? Number(data.shift_id) : null,
    shiftName: data.shift_name ? String(data.shift_name) : null,
    shiftTime: data.shift_time ? String(data.shift_time) : null,
  };
}
